import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Search, Droplets, AlertTriangle, Info, Baby, Heart, Sparkles } from 'lucide-react';
import { ProNavigation } from "@/components/ProNavigation";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const hydrolatsData = [
  {
    id: "camomille",
    name: "Hydrolat de Camomille",
    latinName: "Matricaria recutita",
    emoji: "🌼",
    partUsed: "Fleurs",
    properties: ["Calmante", "Digestive", "Anti-inflammatoire"],
    usageInterne: "Coliques, troubles oculaires",
    usageExterne: "Peaux sensibles, coliques du nourrisson, poussées dentaires",
    energy: "Calme les colériques, rancuniers et frustrés",
    contraindications: ["Allergie possible aux Astéracées"],
    keywords: ["colique", "oculaire", "yeux", "peau sensible", "bébé", "nourrisson", "dent", "calme", "nerveux", "irritation"]
  },
  {
    id: "fleur-oranger",
    name: "Hydrolat de Fleur d'Oranger",
    latinName: "Citrus aurantium amara",
    emoji: "🍊",
    partUsed: "Fleurs",
    properties: ["Sédatif", "Antidépresseur", "Digestif"],
    usageInterne: "Sommeil, stress, ménopause, cœur",
    usageExterne: "Peau irritée, choc émotionnel, burn-out",
    usageBebe: "Soin apaisant, calme agitations",
    energy: "Pour ceux qui manquent de joie ou ont des blessures d'enfance",
    contraindications: [],
    keywords: ["sommeil", "stress", "ménopause", "coeur", "peau", "irrité", "burn-out", "émotionnel", "bébé", "calme", "joie", "anxiété"]
  },
  {
    id: "menthe-poivree",
    name: "Hydrolat de Menthe Poivrée",
    latinName: "Mentha x piperita",
    emoji: "🌱",
    partUsed: "Parties aériennes",
    properties: ["Tonique", "Rafraîchissant", "Antalgique", "Antiviral"],
    usageInterne: "Migraines, nausées, eczéma, zona",
    usageExterne: "Jambes lourdes, coups de soleil, peaux fatiguées",
    cure: "5 jours/sem pendant 3 sem + pause 1 sem OU Tous les jours pendant 3 sem + pause",
    contraindications: ["Éviter chez les enfants en bas âge", "Prudence femmes enceintes"],
    keywords: ["migraine", "nausée", "eczéma", "zona", "jambes lourdes", "soleil", "fatigue", "tonique", "rafraîchissant", "digestion"]
  },
  {
    id: "rose",
    name: "Hydrolat de Rose",
    latinName: "Rosa damascena / Rosa centifolia",
    emoji: "🌹",
    partUsed: "Pétales de rose",
    properties: ["Apaisant", "Tonifiant", "Régénérant cutané", "Relaxant émotionnellement"],
    usageInterne: "Dilué dans l'eau pour la détente et gestion du stress (quantités modérées)",
    usageExterne: "Tonique pour la peau, lotion tonique visage, peaux sensibles, rafraîchissant",
    contraindications: [],
    keywords: ["peau", "cutané", "détente", "stress", "relaxant", "tonique", "visage", "sensible", "régénérant", "anti-âge"]
  },
  {
    id: "lavande",
    name: "Hydrolat de Lavande",
    latinName: "Lavandula angustifolia",
    emoji: "💜",
    partUsed: "Fleurs",
    properties: ["Apaisant", "Relaxant", "Antiseptique doux", "Anti-inflammatoire léger"],
    usageInterne: "Très faible quantité dans l'eau pour relaxation et sommeil (avec prudence)",
    usageExterne: "Spray calmant pour peau, irritations cutanées, brumisation d'ambiance relaxante",
    contraindications: [],
    keywords: ["relaxant", "sommeil", "peau", "irritation", "calme", "antiseptique", "ambiance", "stress", "anxiété"]
  },
  {
    id: "hamamelis",
    name: "Hydrolat d'Hamamélis",
    latinName: "Hamamelis virginiana",
    emoji: "🍂",
    partUsed: "Feuilles et écorce",
    properties: ["Astringent", "Anti-inflammatoire", "Tonique cutané"],
    usageInterne: "Rarement consommé ; usage interne possible avec avis professionnel",
    usageExterne: "Tonique pour peau grasse ou sujette aux imperfections, compresses, apaisement après rasage",
    contraindications: ["Usage interne avec prudence"],
    keywords: ["astringent", "peau grasse", "imperfection", "acné", "rasage", "tonique", "inflammatoire", "circulatoire"]
  },
  {
    id: "bleuet",
    name: "Hydrolat de Bleuets",
    latinName: "Centaurea cyanus",
    emoji: "💙",
    partUsed: "Fleurs",
    properties: ["Apaisant", "Décongestionnant", "Tonique"],
    usageInterne: "Parfois ajouté à l'eau pour hydratation douce",
    usageExterne: "Compresses pour yeux fatigués ou irrités, peaux sensibles",
    contraindications: [],
    keywords: ["yeux", "oculaire", "fatigué", "irrité", "peau sensible", "décongestionnant", "compresse"]
  },
  {
    id: "laurier-cerise",
    name: "Hydrolat de Laurier-cerise",
    latinName: "Prunus laurocerasus",
    emoji: "🍃",
    partUsed: "Feuilles et fleurs",
    properties: ["Tonique", "Astringent léger"],
    usageInterne: "Usage interne non courant ; privilégier externe",
    usageExterne: "Tonique cutané, brume rafraîchissante",
    contraindications: ["Usage externe principalement"],
    keywords: ["tonique", "cutané", "rafraîchissant", "astringent"]
  },
  {
    id: "fenouil",
    name: "Hydrolat de Fenouil",
    latinName: "Foeniculum vulgare",
    emoji: "🌿",
    partUsed: "Graines et feuilles",
    properties: ["Digestif doux", "Carminatif", "Antispasmodique"],
    usageInterne: "Dilué dans l'eau pour soutenir la digestion et réduire les ballonnements",
    usageExterne: "Tonique circulatoire léger, compresses",
    contraindications: ["Éviter en cas de cancer hormonodépendant"],
    keywords: ["digestion", "ballonnement", "digestif", "carminatif", "spasme", "circulatoire", "estomac", "intestin"]
  }
];

export default function HydrolatsLibrary() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredHydrolats = hydrolatsData.filter(hydrolat =>
    hydrolat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hydrolat.latinName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hydrolat.properties.some(p => p.toLowerCase().includes(searchTerm.toLowerCase())) ||
    hydrolat.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-hero py-8 px-4">
      <ProNavigation />
      
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate('/treatments-library')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Bibliothèque</span>
            <span className="sm:hidden">Retour</span>
          </Button>
          <h1 className="text-xl sm:text-3xl font-bold text-primary flex items-center gap-2">
            <Droplets className="w-6 h-6 sm:w-8 sm:h-8" />
            Les Hydrolats
          </h1>
          <div className="hidden sm:block w-[180px]" />
        </div>

        {/* Introduction */}
        <Card className="mb-6 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-950/20 dark:to-blue-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Droplets className="w-6 h-6 text-cyan-600" />
              🔹 Définition
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg">
              Le mot <strong>hydrolat</strong> vient de <em>hydro</em> (eau) + <em>latte</em> (lait) → 💧🍼 <strong>eau laiteuse</strong>
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>🔹 C'est le résidu aqueux de la distillation des plantes</li>
              <li>🔹 Contient une petite quantité d'huile essentielle → plus doux 🌼</li>
              <li>🔹 Aussi appelé : hydrosol, aquarome</li>
            </ul>
            
            <div className="mt-4 p-4 bg-background/50 rounded-lg">
              <h4 className="font-semibold mb-2">💡 Différence avec l'eau florale</h4>
              <ul className="space-y-1 text-sm">
                <li>🌸 <strong>Eau florale</strong> = distillation des fleurs uniquement</li>
                <li>🌿 <strong>Hydrolat</strong> = distillation de toute la plante (feuilles, tiges, écorces...)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Informations pratiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Baby className="w-4 h-4 text-pink-500" />
                Utilisation
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p>✔️ Pour tous : enfants, femmes enceintes, personnes sensibles</p>
              <p>✔️ Usage interne : boisson 🌿</p>
              <p>✔️ Usage externe : brumisation, compresses, bain, cosmétique</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Conservation
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p>⏳ Se conserve 2 ans max</p>
              <p>💡 Sensible à : lumière 🌞, chaleur 🔥, air 💨</p>
              <p>📦 Flacon foncé au réfrigérateur ❄️</p>
              <p>👃 Mauvaise odeur = plus utilisable ❌</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-green-500" />
                Choisir un bon hydrolat
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p>🌱 Bio, non coupé à l'eau ou agents synthétiques</p>
              <p>🧪 Vérifier la qualité sur l'étiquette</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-500" />
                Posologie
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div>
                <p className="font-medium">☕ Usage interne :</p>
                <p className="text-xs">1 c. à café dans un verre d'eau (2-3×/jour)</p>
                <p className="text-xs">ou 1 c. à soupe dans 1L d'eau/jour</p>
              </div>
              <div>
                <p className="font-medium">💆‍♀️ Usage externe :</p>
                <p className="text-xs">Brumisation, bains, compresses, cosmétique</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recherche */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Rechercher un hydrolat (nom, propriété, indication...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Section des 3 hydrolats polyvalents */}
        <Card className="mb-6 border-2 border-green-200 dark:border-green-900">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">
              🟩 Les 3 Hydrolats Polyvalents
            </CardTitle>
            <CardDescription>
              Les indispensables à avoir dans sa trousse
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {hydrolatsData.slice(0, 3).map((hydrolat) => (
                <Card key={hydrolat.id} className="bg-green-50/50 dark:bg-green-950/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-2xl">{hydrolat.emoji}</span>
                      {hydrolat.name.replace('Hydrolat de ', '')}
                    </CardTitle>
                    <CardDescription className="italic text-xs">
                      {hydrolat.latinName}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {hydrolat.properties.map((prop, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {prop}
                        </Badge>
                      ))}
                    </div>
                    <p><strong>Usage interne:</strong> {hydrolat.usageInterne}</p>
                    <p><strong>Usage externe:</strong> {hydrolat.usageExterne}</p>
                    {hydrolat.energy && (
                      <p className="text-xs text-muted-foreground italic">✨ {hydrolat.energy}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Tous les hydrolats */}
        <Card>
          <CardHeader>
            <CardTitle>📖 Tous les Hydrolats</CardTitle>
            <CardDescription>
              {filteredHydrolats.length} hydrolat{filteredHydrolats.length > 1 ? 's' : ''} disponible{filteredHydrolats.length > 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {filteredHydrolats.map((hydrolat) => (
                <AccordionItem key={hydrolat.id} value={hydrolat.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{hydrolat.emoji}</span>
                      <div className="text-left">
                        <span className="font-semibold">{hydrolat.name}</span>
                        <span className="text-sm text-muted-foreground ml-2 italic">
                          {hydrolat.latinName}
                        </span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                      <div>
                        <h4 className="font-medium text-sm mb-2">🌿 Partie utilisée</h4>
                        <p>{hydrolat.partUsed}</p>
                      </div>

                      <div>
                        <h4 className="font-medium text-sm mb-2">🔹 Propriétés</h4>
                        <div className="flex flex-wrap gap-2">
                          {hydrolat.properties.map((prop, idx) => (
                            <Badge key={idx} variant="outline">{prop}</Badge>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-sm mb-1">☕ Usage interne</h4>
                          <p className="text-sm text-muted-foreground">{hydrolat.usageInterne}</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-sm mb-1">💆‍♀️ Usage externe</h4>
                          <p className="text-sm text-muted-foreground">{hydrolat.usageExterne}</p>
                        </div>
                      </div>

                      {hydrolat.usageBebe && (
                        <div>
                          <h4 className="font-medium text-sm mb-1">👶 Usage bébé</h4>
                          <p className="text-sm text-muted-foreground">{hydrolat.usageBebe}</p>
                        </div>
                      )}

                      {hydrolat.cure && (
                        <div className="p-3 bg-primary/5 rounded-lg">
                          <h4 className="font-medium text-sm mb-1">✨ Cure tonique</h4>
                          <p className="text-sm">{hydrolat.cure}</p>
                        </div>
                      )}

                      {hydrolat.energy && (
                        <div className="p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg">
                          <h4 className="font-medium text-sm mb-1">✨ Énergie</h4>
                          <p className="text-sm italic">{hydrolat.energy}</p>
                        </div>
                      )}

                      {hydrolat.contraindications.length > 0 && (
                        <div className="p-3 bg-destructive/10 rounded-lg">
                          <h4 className="font-medium text-sm mb-1 text-destructive">⚠️ Contre-indications</h4>
                          <ul className="text-sm text-destructive">
                            {hydrolat.contraindications.map((ci, idx) => (
                              <li key={idx}>• {ci}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="mt-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
          <CardHeader>
            <CardTitle>🧾 Conclusion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="font-medium text-green-700 dark:text-green-400">💚 Les hydrolats sont :</p>
              <ul className="space-y-1 ml-4">
                <li>✔️ Doux mais efficaces</li>
                <li>✔️ Adaptés aux personnes fragiles</li>
                <li>✔️ Une alternative aux huiles essentielles 🌸</li>
              </ul>
              <p className="text-muted-foreground text-sm mt-2">
                ⚠️ Moins concentrés → effet plus progressif
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
